import { useRef } from 'react';

interface UseTabSwipeOptions {
  pillRef: React.RefObject<HTMLDivElement>;
  isSecondTabActive: boolean;
  switchToFirst: () => void;
  switchToSecond: () => void;
  isDraggingRef?: React.MutableRefObject<boolean>;
  onAfterDrag?: () => void;
  dragThreshold?: number;
  lockThreshold?: number;
}

interface DragState {
  isDragging: boolean;
  isLocked: boolean;
  startX: number;
  startY: number;
  lastX: number;
  startPillX: number;
  pillWidth: number;
}

export function useTabSwipe({
  pillRef,
  isSecondTabActive,
  switchToFirst,
  switchToSecond,
  isDraggingRef,
  onAfterDrag,
  dragThreshold = 30,
  lockThreshold = 8,
}: UseTabSwipeOptions) {
  const onAfterDragRef = useRef(onAfterDrag);
  onAfterDragRef.current = onAfterDrag;

  const dragState = useRef<DragState>({
    isDragging: false,
    isLocked: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    startPillX: 0,
    pillWidth: 0,
  });

  const onTouchStart = (e: React.TouchEvent) => {
    const pill = pillRef.current;
    if (!pill) return;

    const pillWidth = pill.getBoundingClientRect().width;
    const startPillX = isSecondTabActive ? pillWidth : 0;

    dragState.current = {
      isDragging: true,
      isLocked: false,
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      lastX: e.touches[0].clientX,
      startPillX,
      pillWidth,
    };

    pill.style.transition = 'none';
    pill.style.transform = `translateX(${startPillX}px)`;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const state = dragState.current;
    if (!state.isDragging) return;

    const pill = pillRef.current;
    if (!pill) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - state.startX;
    const deltaY = currentY - state.startY;
    state.lastX = currentX;

    if (!state.isLocked) {
      if (Math.abs(deltaX) < lockThreshold && Math.abs(deltaY) < lockThreshold) return;

      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        // vertical scroll won — abort the drag
        state.isDragging = false;
        pill.style.transition = '';
        pill.style.transform = '';
        return;
      }

      state.isLocked = true;
      if (isDraggingRef) isDraggingRef.current = true;
    }

    const newX = Math.max(0, Math.min(state.pillWidth, state.startPillX + deltaX));
    pill.style.transform = `translateX(${newX}px)`;
  };

  const onTouchEnd = () => {
    const state = dragState.current;
    if (!state.isDragging || !state.isLocked) {
      state.isDragging = false;
      const el = pillRef.current;
      if (el) {
        el.style.transition = '';
        el.style.transform = '';
      }
      return;
    }
    state.isDragging = false;

    const pill = pillRef.current;
    if (!pill) return;

    const deltaX = state.lastX - state.startX;
    const { startPillX, pillWidth } = state;

    const shouldSwitchToSecond = !isSecondTabActive && deltaX > dragThreshold;
    const shouldSwitchToFirst = isSecondTabActive && deltaX < -dragThreshold;

    const cleanup = () => {
      if (isDraggingRef) isDraggingRef.current = false;
      const el = pillRef.current;
      if (!el) return;
      if (onAfterDragRef.current) {
        el.style.transition = 'none';
        el.style.transform = '';
        onAfterDragRef.current();
        requestAnimationFrame(() => {
          if (pillRef.current) pillRef.current.style.transition = '';
        });
      } else {
        el.style.transition = '';
        el.style.transform = '';
      }
    };

    if (shouldSwitchToSecond || shouldSwitchToFirst) {
      const targetX = shouldSwitchToSecond ? pillWidth : 0;
      pill.style.transition = 'transform 250ms ease-out';
      pill.style.transform = `translateX(${targetX}px)`;
      if (shouldSwitchToSecond) {
        switchToSecond();
      } else {
        switchToFirst();
      }
      setTimeout(cleanup, 260);
    } else {
      pill.style.transition = 'transform 250ms ease-out';
      pill.style.transform = `translateX(${startPillX}px)`;
      setTimeout(cleanup, 260);
    }
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
}
